import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslations } from 'src/contexts/TranslationContext';
import categoriesData from 'src/backendshared/xnews_categories.json';
import { formatCategoryName } from 'src/utils/categoryFormatter';

interface ArchiveProps {
    lang?: string;
}

// Type definition for the categories object
interface Categories {
    main: string[];
    [key: string]: string[]; // All other category properties are string arrays
}

// Use the imported categories data with the correct type and normalize all keys to lowercase
const rawCategories: Categories = categoriesData;
const categories: Categories = Object.entries(rawCategories).reduce((acc, [key, values]) => {
    acc[key.toLowerCase()] = values.map(value => value.toLowerCase());
    return acc;
}, {} as Categories);

// Debug log to verify data is loaded
console.log('[Archive] Original categories data:', rawCategories);
console.log('[Archive] Normalized categories:', categories);
console.log('[Archive] Categories keys:', Object.keys(categories));
console.log('[Archive] Sample politics category:', categories.politics);

const Archive: React.FC<ArchiveProps> = ({ lang }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language, getUrlLanguagePrefix, translations: t } = useTranslations();
    
    // Get query parameters
    const searchParams = new URLSearchParams(location.search);
    const typeParam = searchParams.get('type');
    const subtypeParam = searchParams.get('subtype');
    
    // Redirect to dates page if both type and subtype are specified
    useEffect(() => {
        if (typeParam && subtypeParam) {
            const prefix = getUrlLanguagePrefix(language);
            navigate(`${prefix}/archive/dates?subtype=${subtypeParam}`);
        }
    }, [typeParam, subtypeParam, navigate, language, getUrlLanguagePrefix]);

    // Get all subcategories from the categories data
    const allSubtypes = Object.entries(categories)
        .filter(([category]) => category !== 'main') // Skip the main category
        .flatMap(([_, subcategories]) => subcategories)
        .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
    
    // Define type to subtype mapping directly from categories
    const typeToSubtypes: Record<string, string[]> = {};
    
    // Populate typeToSubtypes from the categories data
    Object.entries(categories).forEach(([category, subcategories]) => {
        if (category !== 'main') {
            typeToSubtypes[category] = subcategories;
        }
    });
    
    // Log the mappings for debugging
    console.log('[Archive] All subtypes:', allSubtypes);
    console.log('[Archive] Type to subtypes mapping:', typeToSubtypes);
    
    // Filter subtypes based on type parameter
    let displaySubtypes = [...allSubtypes];
    if (typeParam && typeToSubtypes[typeParam.toLowerCase()]) {
        displaySubtypes = typeToSubtypes[typeParam.toLowerCase()];
    }

    // Sort alphabetically
    const sortedSubtypes = [...displaySubtypes].sort();

    const handleSubtypeClick = (subtype: string) => {
        const prefix = getUrlLanguagePrefix(language);
        // Navigate to date selection page
        navigate(`${prefix}/archive/dates?subtype=${subtype}`);
    };

    // Get translated subtype name
    const getSubtypeTranslation = (subtype: string) => {
        // Try multiple cases to find the translation
        const lowerCase = subtype.toLowerCase();
        const upperFirstCase = subtype.charAt(0).toUpperCase() + subtype.slice(1).toLowerCase();
        
        // Try to find a translation in different case formats
        return t?.navigations?.header?.[lowerCase] || 
               t?.navigations?.header?.[subtype] || 
               t?.navigations?.header?.[upperFirstCase] || 
               formatCategoryName(subtype);
    };
    
    // Get translated type name for the title
    const getTypeTitle = () => {
        if (!typeParam) return t?.navigations?.header?.archive || 'News Archive';
        
        return t?.navigations?.header?.[typeParam.toLowerCase()] || 
               typeParam.charAt(0).toUpperCase() + typeParam.slice(1);
    };

    // Create a mapping of categories to their respective subcategories for display
    const categoryMapping: Record<string, string[]> = {
        'politics': typeToSubtypes['politics'] || [],
        'business': typeToSubtypes['business'] || [],
        'world': typeToSubtypes['world'] || [],
        'technology': typeToSubtypes['technology'] || [],
        'entertainment': typeToSubtypes['entertainment'] || [],
        'sports': typeToSubtypes['sports'] || [],
        'health': [...(typeToSubtypes['health'] || []), ...(typeToSubtypes['lifestyle'] || [])],
    };

    // Log the category mapping for debugging
    console.log('[Archive] Category mapping for display:', categoryMapping);

    return (
        <div className="max-w-6xl mx-auto p-8">
            <div className="flex flex-col items-start mb-3">
                {/* Breadcrumb navigation similar to NewsDetail */}
                <div className="flex items-center mb-3 text-lg">
                    <span 
                        onClick={() => {
                            const prefix = getUrlLanguagePrefix(language);
                            navigate(`${prefix}/trending`);
                        }}
                        className="text-blue-600 cursor-pointer hover:underline"
                    >
                        {t?.navigations?.header?.trending || 'Trending'}
                    </span>
                    <span className="text-gray-500 mx-2">→</span>
                    <span className="text-gray-500 mx-1">/</span>
                    <span className="text-gray-800 font-medium">
                        {getTypeTitle()}
                    </span>
                </div>
                
                {/* Main title - larger, prominent and centered */}
                <h1 className="text-3xl md:text-4xl font-bold text-gray-800  self-center mx-auto">
                    {getTypeTitle()}
                </h1>
                
                {/* Clear filter button */}
                {typeParam && <div className="mt-1 self-center">
                    <button 
                        onClick={() => {
                            const prefix = getUrlLanguagePrefix(language);
                            navigate(`${prefix}/archive`);
                        }}
                        className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-md inline-flex items-center text-sm"
                    >
                        <span>Clear filter</span>
                    </button>
                </div>}
            </div>
            
            <div className="bg-gray-50 rounded-xl p-8 shadow-md">
                {!typeParam ? (
                    // Show categorized view when no type filter is applied
                    <div className="space-y-10">
                        {/* News & Politics */}
                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-blue-800 border-b pb-2">News & Politics</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {(categoryMapping['politics'] || []).slice(0, 13).map(subtype => (
                                    <button
                                        key={subtype}
                                        onClick={() => handleSubtypeClick(subtype)}
                                        className="p-3 rounded-lg border text-left hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 border-gray-200 shadow-sm hover:shadow h-full flex items-center"
                                    >
                                        <span className="text-base md:text-lg font-medium break-words hyphens-auto w-full overflow-hidden">{getSubtypeTranslation(subtype)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Business & Economy */}
                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-green-800 border-b pb-2">Business & Economy</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {(categoryMapping['business'] || []).map(subtype => (
                                    <button
                                        key={subtype}
                                        onClick={() => handleSubtypeClick(subtype)}
                                        className="p-3 rounded-lg border text-left hover:bg-green-50 hover:border-green-300 transition-all duration-200 border-gray-200 shadow-sm hover:shadow h-full flex items-center"
                                    >
                                        <span className="text-base md:text-lg font-medium break-words hyphens-auto w-full overflow-hidden">{getSubtypeTranslation(subtype)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* World & Current Events */}
                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-purple-800 border-b pb-2">World & Current Events</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {(categoryMapping['world'] || []).slice(0, 11).map(subtype => (
                                    <button
                                        key={subtype}
                                        onClick={() => handleSubtypeClick(subtype)}
                                        className="p-3 rounded-lg border text-left hover:bg-purple-50 hover:border-purple-300 transition-all duration-200 border-gray-200 shadow-sm hover:shadow h-full flex items-center"
                                    >
                                        <span className="text-base md:text-lg font-medium break-words hyphens-auto w-full overflow-hidden">{getSubtypeTranslation(subtype)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Technology & Science */}
                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-indigo-800 border-b pb-2">Technology & Science</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {(categoryMapping['technology'] || []).slice(0, 12).map(subtype => (
                                    <button
                                        key={subtype}
                                        onClick={() => handleSubtypeClick(subtype)}
                                        className="p-3 rounded-lg border text-left hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-200 border-gray-200 shadow-sm hover:shadow h-full flex items-center"
                                    >
                                        <span className="text-base md:text-lg font-medium break-words hyphens-auto w-full overflow-hidden">{getSubtypeTranslation(subtype)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Entertainment */}
                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-pink-800 border-b pb-2">Entertainment</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {(categoryMapping['entertainment'] || []).map(subtype => (
                                    <button
                                        key={subtype}
                                        onClick={() => handleSubtypeClick(subtype)}
                                        className="p-3 rounded-lg border text-left hover:bg-pink-50 hover:border-pink-300 transition-all duration-200 border-gray-200 shadow-sm hover:shadow h-full flex items-center"
                                    >
                                        <span className="text-base md:text-lg font-medium break-words hyphens-auto w-full overflow-hidden">{getSubtypeTranslation(subtype)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Sports */}
                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-red-800 border-b pb-2">Sports</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {(categoryMapping['sports'] || []).slice(0, 16).map(subtype => (
                                    <button
                                        key={subtype}
                                        onClick={() => handleSubtypeClick(subtype)}
                                        className="p-3 rounded-lg border text-left hover:bg-red-50 hover:border-red-300 transition-all duration-200 border-gray-200 shadow-sm hover:shadow h-full flex items-center"
                                    >
                                        <span className="text-base md:text-lg font-medium break-words hyphens-auto w-full overflow-hidden">{getSubtypeTranslation(subtype)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Health & Lifestyle */}
                        <div>
                            <h2 className="text-2xl font-bold mb-4 text-teal-800 border-b pb-2">Health & Lifestyle</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {(categoryMapping['health'] || []).slice(0, 10).map(subtype => (
                                    <button
                                        key={subtype}
                                        onClick={() => handleSubtypeClick(subtype)}
                                        className="p-3 rounded-lg border text-left hover:bg-teal-50 hover:border-teal-300 transition-all duration-200 border-gray-200 shadow-sm hover:shadow h-full flex items-center"
                                    >
                                        <span className="text-base md:text-lg font-medium break-words hyphens-auto w-full overflow-hidden">{getSubtypeTranslation(subtype)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    // Show filtered view when type filter is applied
                    <div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {sortedSubtypes.map(subtype => (
                                <button
                                    key={subtype}
                                    onClick={() => handleSubtypeClick(subtype)}
                                    className="p-3 rounded-lg border text-left hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 border-gray-200 shadow-sm hover:shadow h-full flex items-center"
                                >
                                    <span className="text-base md:text-lg font-medium break-words hyphens-auto w-full overflow-hidden">{getSubtypeTranslation(subtype)}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Archive; 